import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, Check, Info } from 'lucide-react';

/**
 * Renders the "Embed" tab content of ChatbotDetailPage.
 * Extracted to its own component to keep ChatbotDetailPage.jsx maintainable.
 *
 * Props are intentionally explicit (no Context) — they mirror the variables
 * the original inline JSX read from its parent scope.
 */
export const EmbedTab = ({
  chatbot,
  formData,
  theme,
  embedCode,
  copied,
  copyToClipboard,
}) => {
  return (
    <Card className="bg-white border-slate-200 shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-slate-900">Embed Code</CardTitle>
          <Button
            onClick={() => copyToClipboard(embedCode)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="copy-embed-btn"
          >
            {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
            {copied ? 'Copied!' : 'Copy Code'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Info Banner */}
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
          <p className="text-sm text-blue-700 flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span><strong>Good news!</strong> Once you add this embed code, any changes you make to your AI Employee settings will apply automatically. No need to update the code again.</span>
          </p>
        </div>

        {/* Position Info */}
        <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
          <p className="text-sm text-slate-600">
            <strong>Widget Position:</strong> {theme.position === 'bottom-left' ? 'Bottom Left' : 'Bottom Right'} —
            <span className="text-slate-500"> Change this in the Theme tab.</span>
          </p>
        </div>

        <p className="text-sm text-slate-600">Copy this code and paste it into your website&apos;s HTML, just before the closing &lt;/body&gt; tag.</p>
        <div className="relative">
          <pre className="p-4 rounded-lg bg-slate-100 border border-slate-200 text-sm text-emerald-700 overflow-x-auto font-mono pr-12">{embedCode}</pre>
        </div>

        <div className="pt-4 border-t border-slate-200">
          <h4 className="text-sm font-medium text-slate-900 mb-2">API Key</h4>
          <div className="flex items-center gap-2">
            <code className="flex-1 p-2 rounded bg-slate-100 border border-slate-200 text-sm text-slate-600 font-mono overflow-x-auto">{chatbot.api_key}</code>
            <Button variant="ghost" size="icon" className="text-slate-500 hover:text-slate-900 flex-shrink-0" onClick={() => copyToClipboard(chatbot.api_key)}><Copy className="w-4 h-4" /></Button>
          </div>
        </div>

        {chatbot.status !== 'active' && formData.status !== 'active' && (
          <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
            <p className="text-sm text-amber-700">⚠️ Your AI agent is currently in &quot;{formData.status || chatbot.status}&quot; status. Toggle it to &quot;Active&quot; above for the widget to work.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmbedTab;
